import React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { z } from 'zod'
import { CognitoAuthService } from '../services/cognitoAuth'
import clientConverge from '../assets/client-converge.png'
import './LoginPage.css'

const resetPasswordSchema = z.object({
  username: z.string()
    .transform((val) => val.trim())
    .refine((val) => val.length > 0, 'Username is required'),
  verificationCode: z.string()
    .transform((val) => val.trim())
    .refine((val) => val.length > 0, 'Verification code is required')
    .refine((val) => /^\d{6}$/.test(val), 'Verification code must be 6 digits'),
  newPassword: z.string()
    .transform((val) => val.trim())
    .refine((val) => val.length > 0, 'New password is required')
    .refine((val) => val.length >= 8, 'Password must be at least 8 characters')
    .refine((val) => val.length <= 50, 'Password must be less than 50 characters')
    .refine((val) => /[A-Z]/.test(val), 'Password must contain at least one uppercase letter')
    .refine((val) => /[a-z]/.test(val), 'Password must contain at least one lowercase letter')
    .refine((val) => /\d/.test(val), 'Password must contain at least one number')
    .refine((val) => /[!@#$%^&*(),.?":{}|<>]/.test(val), 'Password must contain at least one special character'),
  confirmPassword: z.string()
    .transform((val) => val.trim())
    .refine((val) => val.length > 0, 'Please confirm your password'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
})

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const location = useLocation()

  // Get username from navigation state or require manual entry
  const initialUsername = (location.state as any)?.username || ''

  // State for error handling and loading
  const [resetError, setResetError] = React.useState<string>('')
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const [isPasswordReset, setIsPasswordReset] = React.useState<boolean>(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onBlur',
    defaultValues: {
      username: initialUsername,
      verificationCode: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const watchedPassword = watch('newPassword')

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      // Clear any previous errors and start loading
      setResetError('')
      setIsSubmitting(true)

      const { username, verificationCode, newPassword } = data

      const trimmedUsername = username.trim()
      const trimmedCode = verificationCode.trim()
      const trimmedPassword = newPassword.trim()

      if (!trimmedUsername || !trimmedCode || !trimmedPassword) {
        setResetError('All fields are required')
        setIsSubmitting(false)
        return
      }

      await CognitoAuthService.confirmPasswordReset(
        trimmedUsername,
        trimmedCode,
        trimmedPassword
      )

      // Success - show confirmation
      setIsPasswordReset(true)
      setIsSubmitting(false)

    } catch (error: any) {
      console.error('Reset password error:', error)

      // Provide more specific error messages
      let errorMessage = 'Failed to reset password. Please try again.'

      // Handle both legacy __type and modern name properties
      const errorType = error.__type || error.name || error.code
      console.error('Detected error type:', errorType)

      switch (errorType) {
        case 'UserNotFoundException':
        case 'UserNotFound':
          errorMessage = 'Username not found. Please check your username and try again.'
          break
        case 'CodeMismatchException':
          errorMessage = 'Invalid verification code. Please check the code and try again.'
          break
        case 'ExpiredCodeException':
          errorMessage = 'Verification code has expired. Please request a new code.'
          break
        case 'InvalidParameterException':
        case 'InvalidParameter':
          errorMessage = 'Invalid input. Please check your information and try again.'
          break
        case 'InvalidPasswordException':
          errorMessage = 'Password does not meet the required criteria. Please check the password requirements.'
          break
        case 'LimitExceededException':
        case 'TooManyRequestsException':
          errorMessage = 'Too many attempts. Please wait a few minutes before trying again.'
          break
        case 'NotAuthorizedException':
          errorMessage = 'Password reset is not authorized for this account. Please contact support.'
          break
        case 'InternalErrorException':
        case 'InternalError':
          errorMessage = 'An internal error occurred. Please try again later.'
          break
        default:
          if (error.message) {
            errorMessage = error.message
          }
          break
      }

      setResetError(errorMessage)
      setIsSubmitting(false)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login', { replace: true })
  }

  const requestNewCode = () => {
    navigate('/forgot-password', { replace: true })
  }

  if (isPasswordReset) {
    return (
      <div className="login-background flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full">
          {/* Header with Logo and Branding */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white shield-icon-container">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="lock-icon">
                <path d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <div className="mx-auto mb-4 flex items-center justify-center">
              <img src={clientConverge} alt="PRESIDIO" className="presidio-logo-image" />
            </div>
            <h2 className="text-2xl font-semibold mb-3 text-gray-900">
              Password Reset Successful
            </h2>
            <p className="text-gray-500 text-sm">Your password has been successfully updated</p>
          </div>

          {/* Success Message */}
          <div className="text-center mb-8">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg shadow-sm mb-6">
              <div className="flex items-start justify-center">
                <div className="text-center">
                  <p className="text-sm text-green-700 font-medium mb-2">
                    Password reset completed successfully!
                  </p>
                  <p className="text-xs text-green-600">
                    You can now sign in with your new password.
                  </p>
                </div>
              </div>
            </div>

            <button
              onClick={handleBackToLogin}
              className="w-full py-3 text-white font-medium rounded-lg text-sm transition-all duration-200 sign-in-button hover:shadow-lg hover:opacity-90"
            >
              Back to Sign In
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="login-background flex items-center justify-center p-6">
      <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-lg w-full">
        {/* Header with Logo and Branding */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center text-white shield-icon-container">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor" className="lock-icon">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z"/>
            </svg>
          </div>
          <div className="mx-auto mb-4 flex items-center justify-center">
            <img src={clientConverge} alt="PRESIDIO" className="presidio-logo-image" />
          </div>
          <h2 className="text-2xl font-semibold mb-3 text-gray-900">
            Reset Password
          </h2>
          <p className="text-gray-500 text-sm">Enter the verification code and your new password</p>
        </div>

        {/* Reset Password Form */}
        <div className="login-form">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
            {/* Validation Summary */}
            {(errors.username || errors.verificationCode || errors.newPassword || errors.confirmPassword) && (
              <div className="p-3 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
                <div className="flex items-start">
                  <span className="text-yellow-500 text-sm mr-2">ℹ</span>
                  <div>
                    <h4 className="text-sm font-medium text-yellow-800">Please fix the following:</h4>
                    <ul className="mt-1 text-xs text-yellow-700 list-disc list-inside space-y-1">
                      {errors.username && <li>Username: {errors.username.message}</li>}
                      {errors.verificationCode && <li>Code: {errors.verificationCode.message}</li>}
                      {errors.newPassword && <li>Password: {errors.newPassword.message}</li>}
                      {errors.confirmPassword && <li>Confirm: {errors.confirmPassword.message}</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                Username or Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <input
                  {...register('username')}
                  type="text"
                  autoComplete="username"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    errors.username
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter your username or email"
                  onBlur={(e) => {
                    e.target.value = e.target.value.trim()
                  }}
                />
              </div>
              {errors.username && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.username.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                Verification Code
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 4V2a1 1 0 011-1h8a1 1 0 011 1v2h3a1 1 0 011 1v14a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1h3z"/>
                  </svg>
                </div>
                <input
                  {...register('verificationCode')}
                  type="text"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    errors.verificationCode
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter 6-digit code from email"
                  onBlur={(e) => {
                    e.target.value = e.target.value.trim()
                  }}
                />
              </div>
              {errors.verificationCode && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.verificationCode.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  {...register('newPassword')}
                  type="password"
                  autoComplete="new-password"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    errors.newPassword
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Enter your new password"
                  onBlur={(e) => {
                    e.target.value = e.target.value.trim()
                  }}
                />
              </div>
              {errors.newPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.newPassword.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-gray-700 mb-2 text-sm font-medium">
                Confirm New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <input
                  {...register('confirmPassword')}
                  type="password"
                  autoComplete="new-password"
                  className={`w-full pl-10 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
                    errors.confirmPassword
                      ? 'border-red-300'
                      : 'border-gray-300'
                  }`}
                  placeholder="Confirm your new password"
                  onBlur={(e) => {
                    e.target.value = e.target.value.trim()
                  }}
                />
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>

            {/* Password Requirements */}
            {watchedPassword && (
              <div className="p-3 bg-blue-50 border-l-4 border-blue-400 rounded-lg">
                <div className="text-sm">
                  <h4 className="text-blue-800 font-medium mb-2">Password Requirements:</h4>
                  <ul className="text-xs text-blue-700 space-y-1">
                    <li className={watchedPassword.length >= 8 ? 'text-green-600' : ''}>
                      ✓ At least 8 characters
                    </li>
                    <li className={/[A-Z]/.test(watchedPassword) ? 'text-green-600' : ''}>
                      ✓ One uppercase letter
                    </li>
                    <li className={/[a-z]/.test(watchedPassword) ? 'text-green-600' : ''}>
                      ✓ One lowercase letter
                    </li>
                    <li className={/\d/.test(watchedPassword) ? 'text-green-600' : ''}>
                      ✓ One number
                    </li>
                    <li className={/[!@#$%^&*(),.?":{}|<>]/.test(watchedPassword) ? 'text-green-600' : ''}>
                      ✓ One special character
                    </li>
                  </ul>
                </div>
              </div>
            )}

            {/* Reset Error Message */}
            {resetError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-sm">
                <div className="flex items-start">
                  <div>
                    <p className="text-sm text-red-700 font-medium">
                      {resetError}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full py-3 text-white font-medium rounded-lg text-sm transition-all duration-200 sign-in-button ${
                isSubmitting
                  ? 'cursor-not-allowed opacity-75'
                  : 'hover:shadow-lg hover:opacity-90'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Resetting Password...
                </div>
              ) : (
                'Reset Password'
              )}
            </button>

            <div className="flex items-center justify-between text-sm">
              <button
                type="button"
                onClick={requestNewCode}
                className="text-teal-500 hover:text-teal-600"
              >
                Didn't receive code?
              </button>
              <Link to="/login" className="text-teal-500 hover:text-teal-600">
                Back to Sign In
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ResetPasswordPage
